import { create } from 'zustand';
import type { Session } from '@/types/session';
import { getSessions, createSession as apiCreateSession, deleteSession as apiDeleteSession } from '@/api/sessions';

interface SessionState {
  sessions: Session[];
  currentSession: Session | null;
  loading: boolean;
  error: string | null;
}

interface SessionActions {
  loadSessions: () => Promise<void>;
  createSession: (title?: string) => Promise<Session | null>;
  switchSession: (session_id: string) => void;
  deleteSession: (session_id: string) => Promise<void>;
  setCurrentSession: (session: Session | null) => void;
  updateSessionTitle: (session_id: string, title: string) => void;
  clearError: () => void;
}

type SessionStore = SessionState & SessionActions;

export const useSessionStore = create<SessionStore>((set, get) => ({
  sessions: [],
  currentSession: null,
  loading: false,
  error: null,

  loadSessions: async () => {
    set({ loading: true, error: null });
    try {
      const sessions = await getSessions();
      set((state) => ({
        sessions,
        currentSession: state.currentSession || sessions[0] || null,
        loading: false,
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : '加载会话失败';
      set({ error: message, loading: false });
    }
  },

  createSession: async (title?: string) => {
    set({ loading: true, error: null });
    try {
      const response = await apiCreateSession(title ? { title } : undefined);
      const newSession: Session = {
        session_id: response.session_id,
        title: response.title,
        messages: [],
        created_at: response.created_at,
        updated_at: response.updated_at || response.created_at,
        system_prompt_version: response.system_prompt_version,
        tenant_id: response.tenant_id,
        user_id: response.user_id,
        user_name: response.user_name,
        user_display_name: response.user_display_name,
      };
      const updatedSessions = [newSession, ...get().sessions];
      set({
        sessions: updatedSessions,
        currentSession: newSession,
        loading: false,
      });
      return newSession;
    } catch (err) {
      const message = err instanceof Error ? err.message : '创建会话失败';
      set({ error: message, loading: false });
      return null;
    }
  },

  switchSession: (session_id: string) => {
    const session = get().sessions.find((s) => s.session_id === session_id) || null;
    set({ currentSession: session });
  },

  deleteSession: async (session_id: string) => {
    set({ loading: true, error: null });
    try {
      await apiDeleteSession(session_id);
      const updatedSessions = get().sessions.filter((s) => s.session_id !== session_id);
      let newCurrent = get().currentSession;
      if (newCurrent?.session_id === session_id) {
        newCurrent = updatedSessions.length > 0 ? updatedSessions[0] : null;
      }
      set({ sessions: updatedSessions, currentSession: newCurrent, loading: false });
    } catch (err) {
      const message = err instanceof Error ? err.message : '删除会话失败';
      set({ error: message, loading: false });
    }
  },

  setCurrentSession: (session: Session | null) => {
    set({ currentSession: session });
  },

  updateSessionTitle: (session_id: string, title: string) => {
    set((state) => {
      const sessions = state.sessions.map((session) =>
        session.session_id === session_id ? { ...session, title } : session
      );
      const currentSession =
        state.currentSession?.session_id === session_id
          ? { ...state.currentSession, title }
          : state.currentSession;
      return { sessions, currentSession };
    });
  },

  clearError: () => {
    set({ error: null });
  },
}));
